import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useActivePatients, useOrientedPatients } from '@/stores/patients';
import { respondToTransfer } from '@/services/api/transfer';
import type { PatientSnapshot } from '@/services/api/transfer';

type IncomingTransfer = {
  id: string;
  fromUid: string;
  fromDisplayName: string;
  patientSnapshots: PatientSnapshot[];
};

function toTransfer(id: string, data: DocumentData): IncomingTransfer {
  return {
    id,
    fromUid: String(data.fromUid ?? ''),
    fromDisplayName: String(data.fromDisplayName ?? 'Un collègue'),
    patientSnapshots: Array.isArray(data.patientSnapshots)
      ? (data.patientSnapshots as PatientSnapshot[])
      : [],
  };
}

export function IncomingTransferWatcher() {
  const profile = useAuthStore((s) => s.profile);
  const verifyShift = useShiftStore((s) => s.verifyCurrentShift);
  const refreshActive = useActivePatients((s) => s.refresh);
  const refreshOriented = useOrientedPatients((s) => s.refresh);

  const [pending, setPending] = useState<IncomingTransfer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile?.uid) return;
    const q = query(
      collection(db, 'users', profile.uid, 'incomingTransfers'),
      where('status', '==', 'pending'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => toTransfer(d.id, d.data()))
        .sort((a, b) => a.id.localeCompare(b.id));
      setPending((prev) => {
        if (prev && docs.some((d) => d.id === prev.id)) return prev;
        return docs[0] ?? null;
      });
    });
    return () => unsub();
  }, [profile?.uid]);

  async function onRespond(action: 'accept' | 'decline') {
    if (!pending || !profile) return;
    setBusy(true);
    setError(null);
    try {
      await respondToTransfer({ transferId: pending.id, action });
      if (action === 'accept') {
        await Promise.all([
          verifyShift(profile.uid),
          refreshActive(profile.uid),
          refreshOriented(profile.uid),
        ]);
      }
      setPending(null);
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!pending) return null;

  const count = pending.patientSnapshots.length;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-brand-dark text-white rounded-2xl shadow-card max-w-md w-full p-6 flex flex-col gap-4"
      >
        <h2 className="text-lg font-semibold">Transfert entrant</h2>
        <p className="text-sm text-white/80">
          {pending.fromDisplayName} veut vous transférer {count} patient
          {count > 1 ? 's' : ''}.
        </p>
        <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto">
          {pending.patientSnapshots.map((s, i) => {
            const name = `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim();
            const sub = [s.civ, s.dx].filter(Boolean).join(' · ');
            return (
              <li
                key={i}
                className="bg-white/5 rounded-xl px-3 py-2 text-sm leading-tight"
              >
                <div className="font-medium">{name || '—'}</div>
                <div className="text-xs text-white/60">{sub || '—'}</div>
              </li>
            );
          })}
        </ul>
        {error && <p className="text-sm text-red-300">{error}</p>}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            className="flex-1 rounded-pill bg-white/15 text-white py-2 font-semibold disabled:opacity-50"
            disabled={busy}
            onClick={() => void onRespond('decline')}
          >
            {busy ? '…' : 'Refuser'}
          </button>
          <button
            type="button"
            className="flex-1 rounded-pill bg-brand-orange text-white py-2 font-semibold disabled:opacity-50"
            disabled={busy}
            onClick={() => void onRespond('accept')}
          >
            {busy ? '…' : 'Accepter'}
          </button>
        </div>
      </div>
    </div>
  );
}

function humanizeError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  if (raw.includes('already_resolved')) return 'Transfert déjà traité.';
  if (raw.includes('not_found')) return 'Transfert introuvable.';
  return 'Une erreur est survenue. Réessayez.';
}
